import { inject, Injectable } from '@angular/core';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  AccountingEntry,
  Invoice,
  JEConfirmationData,
  Journey,
  LicenseUserRate,
  MatchRequest,
  Movement,
  MovementType,
  Season,
  Team,
  User,
} from '@models';
import { excludeEntriesLinkedToMovements } from '@utils';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { ClubsService } from './club.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { SeasonsService } from './seasons.service';
import { UserService } from './current-user.service';
import { TenantContextService } from './tenant-context.service';

/**
 * Accounting entries store `fiscalYear` inconsistently: sometimes as a single
 * year ("2026") and sometimes as a full season string ("2025-2026"). Match a
 * season ("2025-2026") against both formats.
 */
function matchesSeason(fiscalYear: string | undefined, season: string): boolean {
  if (!fiscalYear) return false;
  return fiscalYear === season || fiscalYear === season.split('-')[1];
}

@Injectable({
  providedIn: 'root',
})
export class ClubManagerService {
  private readonly firestore = inject(FirestoreService);
  private readonly clubService = inject(ClubsService);
  public readonly seasonsService = inject(SeasonsService);
  private readonly usersService = inject(UserService);
  private readonly seasonManagerService = inject(SeasonManagerService);
  private readonly functions = inject(Functions);
  private readonly tenantContext = inject(TenantContextService);
  public club$ = this.usersService.user$.pipe(
    switchMap((user) =>
      this.clubService.all$.pipe(
        map((clubs) =>
          user.clubId
            ? (clubs.find((c) => c.id === user.clubId) ?? clubs[0] ?? null)
            : (clubs[0] ?? null),
        ),
      ),
    ),
    filter(Boolean),
    shareReplay(1),
  );

  public clubBalance$ = combineLatest([
    this.club$,
    this.seasonManagerService.season$,
  ]).pipe(
    switchMap(([club, season]) => {
      const col = this.tenantContext.collections;
      if (!club || !season)
        return of({
          movements: [] as Movement[],
          entries: [] as AccountingEntry[],
        });
      return combineLatest([
        this.firestore.listDocuments<Movement>(
          col.MOVEMENTS,
          where('clubId', '==', club.id),
          where('seasonYear', '==', season),
        ),
        this.firestore.listDocuments<AccountingEntry>(
          col.ACCOUNTING,
          where('clubId', '==', club.id),
        ),
      ]).pipe(
        map(([movements, entries]) => ({
          movements,
          // Manual accounting entries (e.g. bank-transfer income) also count
          // towards the club balance — same criterion as ClubsService.
          // Entries registered from a movement (`movementId`) are skipped:
          // that movement is already counted.
          entries: excludeEntriesLinkedToMovements(
            entries.filter(
              (e) =>
                e.clubId === club.id &&
                (e.seasonYear
                  ? e.seasonYear === season
                  : matchesSeason(e.fiscalYear, season)),
            ),
            movements,
          ),
        })),
      );
    }),
    map(({ movements, entries }) => {
      const sortedMovements = [...movements].sort((a, b) => {
        const dateA = a.date
          ? new Date(a.date).getTime()
          : new Date((a as any).createdAt || 0).getTime();
        const dateB = b.date
          ? new Date(b.date).getTime()
          : new Date((b as any).createdAt || 0).getTime();
        return dateB - dateA;
      });
      const expense =
        movements
          .filter((m) => m.movementType === MovementType.EXPENDITURE)
          .reduce((acc, m) => acc + +m.amount, 0) +
        entries
          .filter((e) => e.type === 'EXPENDITURE')
          .reduce((acc, e) => acc + +(e.totalAmount ?? e.amount), 0);
      const income =
        movements
          .filter((m) => m.movementType === MovementType.INCOME)
          .reduce((acc, m) => acc + +m.amount, 0) +
        entries
          .filter((e) => e.type === 'INCOME')
          .reduce((acc, e) => acc + +(e.totalAmount ?? e.amount), 0);

      return {
        movements: sortedMovements,
        expense,
        income,
        balance: income - expense,
      };
    }),
    shareReplay(1),
  );

  public myTeams$ = combineLatest([
    this.club$,
    this.seasonManagerService.season$,
    this.seasonsService.all$,
  ]).pipe(
    switchMap(([club, seasonYear, seasons]) => {
      const col = this.tenantContext.collections;
      return this.firestore
        .listDocuments<Team>(
          col.TEAMS,
          where('clubId', '==', club ? club.id : ''),
          where('seasonYear', '==', seasonYear),
        )
        .pipe(
          map((teams) =>
            teams.map((team) => ({
              ...team,
              season: team.seasonId
                ? seasons.find((s) => s.id === team.seasonId)
                : undefined,
            })),
          ),
        );
    }),
    shareReplay(1),
  );

  public myInvoices$ = combineLatest([
    this.club$,
    this.seasonManagerService.season$,
  ]).pipe(
    switchMap(([club, seasonYear]) => {
      const col = this.tenantContext.collections;
      return this.firestore.listDocuments<Invoice>(
        col.INVOICES,
        where('clubId', '==', club ? club.id : ''),
        where('seasonYear', '==', seasonYear),
        orderBy('createdAt', 'desc'),
      );
    }),
    shareReplay(1),
  );

  public myPlayers$ = this.club$.pipe(
    switchMap((club) => {
      const col = this.tenantContext.collections;
      return club
        ? this.firestore.listDocuments<User>(
            col.USERS,
            where('clubId', '==', club.id),
          )
        : of([]);
    }),
    shareReplay(1),
  );

  public myClubPlayersWithLicenses$ = combineLatest([
    this.club$,
    this.seasonManagerService.season$,
  ]).pipe(
    switchMap(([club, seasonYear]) => {
      const col = this.tenantContext.collections;
      return club
        ? this.firestore
            .listDocuments<Team>(
              col.TEAMS,
              where('clubId', '==', club.id),
              where('seasonYear', '==', seasonYear),
            )
            .pipe(map((teams) => ({ teams, seasonYear, col })))
        : of({ teams: [], seasonYear, col });
    }),
    switchMap(({ teams, seasonYear, col }) => {
      if (!teams.length) {
        return of([]);
      }
      const userIds = [
        ...new Set(
          teams.flatMap((t) => [
            ...(t.delegatesIds || []),
            ...(t.playersIds || []),
          ]),
        ),
      ];
      return combineLatest(
        userIds.map((userId) =>
          this.firestore
            .getDocument<User>(col.USERS, userId)
            .pipe(
              switchMap((user) =>
                this.firestore
                  .listDocuments<LicenseUserRate>(
                    [col.USERS, userId, 'user_licenses'].join('/'),
                    where('seasonYear', '==', seasonYear),
                  )
                  .pipe(map((licenses) => ({ user, licenses }))),
              ),
            ),
        ),
      );
    }),
    shareReplay(1),
  );

  getTeam(teamId: string): Observable<Team & { season: Season }> {
    const col = this.tenantContext.collections;
    return combineLatest([
      this.firestore.getDocument<Team>(col.TEAMS, teamId),
      this.seasonsService.all$,
    ]).pipe(
      map(([team, seasons]) => {
        const season = seasons.find((s) => s.id === team.seasonId);
        return { ...team, season: season ?? ({} as Season) };
      }),
      shareReplay(1),
    );
  }

  /**
   * Solicitudes de cambio de fecha PENDIENTES dirigidas (`toId`) a cualquier
   * equipo del club del gestor en la temporada actual. Mismo criterio que
   * `MatchRequestService.myRequests$` para delegados, pero con los equipos del
   * club en vez de los equipos que delega el usuario. El servidor
   * (`answerMatchRequest`) permite responder al MANAGER del club del `toId`.
   *
   * Firestore limita los `in` a 30 valores: se trocea en bloques de 30.
   */
  public myRequests$: Observable<MatchRequest[]> = this.myTeams$.pipe(
    switchMap((teams) => {
      if (!teams || teams.length === 0) return of([] as MatchRequest[]);
      const col = this.tenantContext.collections;
      const teamsIds = teams.map((t) => t.id);
      const chunks: string[][] = [];
      for (let i = 0; i < teamsIds.length; i += 30) {
        chunks.push(teamsIds.slice(i, i + 30));
      }
      return combineLatest(
        chunks.map((chunk) =>
          this.firestore.listDocuments<MatchRequest>(
            col.REQUESTS,
            where('toId', 'in', chunk),
            where('status', '==', 'PENDING'),
          ),
        ),
      ).pipe(map((results) => results.flat()));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  public myAvailableTeams$ = this.myTeams$.pipe(map((t) => t as any[]));
  public myConfirmedTeams$ = this.myTeams$.pipe(map(() => [] as any[]));
  public myPendingJETeams$ = this.myTeams$.pipe(map(() => [] as any[]));
  public myAvailableJETeams$ = this.myTeams$.pipe(map(() => [] as any[]));
  public myConfirmedJETeams$ = this.myTeams$.pipe(map(() => [] as any[]));
  public isAvailableInscription$ = of(false);
  public mySchoolPlayers(
    seasonId?: string,
    clubId?: string,
  ): Observable<User[]> {
    return of([] as User[]);
  }
  public my3aCaffPlayers(seasonId: string, clubId: string): Observable<User[]> {
    return of([]);
  }
  deleteTeam(seasonId: string, journeyId: string, teamId: string) {
    return Promise.resolve();
  }

  public selectedTeam$ = new BehaviorSubject<{
    team: Team;
    season: Season | undefined;
    journey: Journey | undefined;
    confirmedPlayersIds: string[];
  } | null>(null);

  saveTeams(confirmationData: JEConfirmationData[]) {
    const callable = httpsCallable<JEConfirmationData[], void>(
      this.functions,
      'JEConfirmations',
    );
    return callable(confirmationData);
  }

  savePlayers(confirmationData: JEConfirmationData[]) {
    const callable = httpsCallable<JEConfirmationData[], void>(
      this.functions,
      'JEConfirmations',
    );
    return callable(confirmationData);
  }

  updateSelectedTeam(
    team: {
      team: Team;
      season: Season | undefined;
      journey: Journey | undefined;
      confirmedPlayersIds: string[];
    } | null,
  ) {
    this.selectedTeam$.next(team);
  }
}
